/**
 * The direct trade's fixed fee, and the point equivalence that pays it.
 *
 * ## Why a fee at all, and why this large
 *
 * A percentage would be the obvious choice and it would be the wrong one: the
 * direct trade transfers Gold and Cash INTEGRALLY, so a percentage on the offer
 * would silently tax a gift. What the fee is for is not revenue — it is friction.
 * It exists to make spam trades and alt-account laundering cost something real.
 *
 * The size comes from measurement rather than taste (`docs/BALANCE_GOLD.md`).
 * An efficient account with no economic bonuses produces roughly 2 539 Gold per
 * hour, a VIP one roughly 3 809, and an observed account with several economic
 * artifacts reached 10 200–11 880. At 200 000 points the fee is somewhere
 * between a long evening and a full day of play — a real decision, and nowhere
 * near a rounding error for the accounts that would abuse it.
 *
 * ## The point equivalence is NOT an exchange rate
 *
 * `1 Gold = 1 point` and `1 Cash = 10 000 points` exist ONLY to say when this
 * fee has been paid. It is not a conversion between the currencies, not a
 * quotation, not a Cash price, not a gacha price, not an item valuation and not
 * a marketplace rate. Nothing outside this fee may read these constants, and
 * nothing here converts one currency into the other: Gold contributed is
 * destroyed as Gold, Cash contributed is destroyed as Cash.
 */

/** What a completed direct trade costs, in points. Centralised for rebalancing. */
export const DIRECT_TRADE_FEE_POINTS = 200_000n;

/** Fee-only equivalence. See the warning above: this is not an exchange rate. */
export const DIRECT_TRADE_GOLD_POINT_VALUE = 1n;
export const DIRECT_TRADE_CASH_POINT_VALUE = 10_000n;

/** One participant's contribution towards the fee. Never negative, never a float. */
export interface FeeContribution {
  readonly gold: bigint;
  readonly cash: bigint;
}

export const NO_FEE_CONTRIBUTION: FeeContribution = Object.freeze({ gold: 0n, cash: 0n });

/**
 * What a contribution is worth, in points.
 *
 * Refuses a negative component rather than quietly treating it as a credit: a
 * negative contribution would let one side reduce the other's, which is exactly
 * the thing "each participant controls only their own contribution" forbids.
 */
export function feeContributionPoints(contribution: FeeContribution): bigint {
  if (contribution.gold < 0n || contribution.cash < 0n) {
    throw new RangeError('A fee contribution cannot be negative');
  }
  return (
    contribution.gold * DIRECT_TRADE_GOLD_POINT_VALUE +
    contribution.cash * DIRECT_TRADE_CASH_POINT_VALUE
  );
}

/** How the fee stands across both participants. */
export interface FeeStatus {
  readonly totalPoints: bigint;
  readonly requiredPoints: bigint;
  /** Zero once the fee is complete. Never negative — see `overpaid`. */
  readonly remainingPoints: bigint;
  readonly complete: boolean;
  /** True when the two contributions add up to MORE than the fee. */
  readonly overpaid: boolean;
}

/**
 * Adds both contributions and says whether the fee is settled.
 *
 * Exact, and exactly once: there is no change, no rounding and no tolerance.
 * Overpaying is a REFUSAL rather than a donation — a trade that accepted more
 * than the fee would be quietly destroying currency nobody agreed to spend, and
 * "the system kept the difference" is not a rule anybody should have to
 * discover.
 */
export function feeStatus(a: FeeContribution, b: FeeContribution): FeeStatus {
  const totalPoints = feeContributionPoints(a) + feeContributionPoints(b);
  const remaining = DIRECT_TRADE_FEE_POINTS - totalPoints;
  return {
    totalPoints,
    requiredPoints: DIRECT_TRADE_FEE_POINTS,
    remainingPoints: remaining > 0n ? remaining : 0n,
    complete: totalPoints === DIRECT_TRADE_FEE_POINTS,
    overpaid: totalPoints > DIRECT_TRADE_FEE_POINTS,
  };
}
